"use client";

import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";

import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname } from "@/lib/i18n";
import { whatsappMessage } from "@/lib/whatsapp";
import { WhatsAppCta } from "@/components/shared/WhatsAppCta";

// OpenAI partner CTA for OpenAI/ChatGPT blog posts (Jazz, 2026-10-08: every
// article needs an inline CTA after the intro and a closing band). Same shape as
// SonarPartnerCTA. Status wording exactly "OpenAI Select Partner"; no OpenAI logo
// or badge here (the badge lives on /partners/openai-partner/ only).
const PARTNER_HREF = "/partners/openai-partner/";

interface OpenAIPartnerCTAProps {
  /** Page name for the WhatsApp message, e.g. "your OpenAI Partner Network article". */
  source: string;
  /** GA4 "Click location" prefix, e.g. "openai_network_blog". */
  location: string;
  variant?: "band" | "inline";
}

const POINTS = [
  "ChatGPT rollout for your teams, with access and usage rules",
  "Custom agents and integrations built on the OpenAI API",
  "ChatGPT team training, HRD Corp claimable in Malaysia",
];

export default function OpenAIPartnerCTA({
  source,
  location,
  variant = "band",
}: OpenAIPartnerCTAProps) {
  const locale = useLocale();
  const partnerHref = getLocalizedPathname(PARTNER_HREF, locale);
  const wa = whatsappMessage("I'd like to talk about ChatGPT for my company", source);

  if (variant === "inline") {
    return (
      <aside className="not-prose my-8 rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          {t(locale, "An OpenAI Select Partner serving Malaysia and Singapore")}
        </p>
        <p className="mt-2 text-base leading-relaxed text-neutral-emphasis">
          {t(
            locale,
            "We help teams roll out ChatGPT, build on the OpenAI API and run HRD Corp claimable ChatGPT training.",
          )}
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-3 text-sm font-semibold">
          <Link href={partnerHref} className="inline-flex items-center gap-1 text-primary hover:underline">
            {t(locale, "Talk to us about ChatGPT")} <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
          <WhatsAppCta
            message={wa}
            location={`${location}_inline`}
            label={t(locale, "WhatsApp us")}
            className="px-5 py-2"
          />
        </div>
      </aside>
    );
  }

  return (
    <section className="not-prose my-12 overflow-hidden rounded-3xl bg-hero-background px-6 py-10 md:px-12 md:py-14">
      <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
        {t(locale, "An OpenAI Select Partner serving Malaysia and Singapore")}
      </p>
      <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-primary-contrast md:text-3xl">
        {t(locale, "Planning a ChatGPT rollout or an OpenAI API build?")}
      </h3>
      <p className="mt-4 max-w-2xl text-primary-contrast/75">
        {t(
          locale,
          "Anchor Sprint is an OpenAI Select Partner. Tell us what you want ChatGPT to do and which data it will touch. If a task does not need AI, we will say so.",
        )}
      </p>
      <ul className="mt-6 space-y-2">
        {POINTS.map((p) => (
          <li key={p} className="flex items-start gap-3 text-sm text-primary-contrast/85">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
            {t(locale, p)}
          </li>
        ))}
      </ul>
      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <Link
          href={partnerHref}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
        >
          {t(locale, "Talk to us about ChatGPT")}
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
        <WhatsAppCta message={wa} location={`${location}_cta`} label={t(locale, "WhatsApp us")} />
      </div>
    </section>
  );
}
